import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CameraCapture from '../components/CameraCapture';
import { useExpenses } from '../contexts/ExpenseContext';
import { CATEGORIES, TRAVEL_SUBCATEGORIES, ExpenseCategory, TravelSubcategory } from '../types';
import { jsPDF } from 'jspdf';
import { 
  CheckCircle, 
  Loader2,
  AlertCircle,
  ChevronLeft,
  Camera,
  ArrowRight,
  FileImage,
  FileText
} from 'lucide-react';

export default function Scanner() {
  const navigate = useNavigate();
  const { addExpense } = useExpenses();
  
  const [step, setStep] = useState<'camera' | 'details' | 'success'>('camera');
  const [capturedImage, setCapturedImage] = useState<{ blob: Blob; filename: string } | null>(null);
  const [category, setCategory] = useState<ExpenseCategory>('travel');
  const [subcategory, setSubcategory] = useState<TravelSubcategory>('uber');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [scannedCount, setScannedCount] = useState(0);
  const [saveFormat, setSaveFormat] = useState<'image' | 'pdf'>('image');

  const handleCapture = (blob: Blob, filename: string) => {
    setCapturedImage({ blob, filename });
    setStep('details');
  };

  const handleCancel = () => {
    navigate('/');
  };

  const scanAnother = () => {
    // Reset form but keep category for convenience
    setCapturedImage(null);
    setDescription('');
    setAmount('');
    setDate(new Date().toISOString().split('T')[0]);
    setError('');
    setStep('camera');
  };

  const finishScanning = () => {
    navigate('/expenses');
  };

  // Generate a readable filename
  const generateFilename = (ext: string) => {
    const cat = CATEGORIES.find(c => c.value === category);
    const catName = cat?.label || category;
    const dateStr = date.replace(/-/g, '');
    const desc = description 
      ? '_' + description.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20)
      : '';
    return `${catName}${desc}_${dateStr}.${ext}`;
  };

  const convertToPdf = async (imageBlob: Blob): Promise<{ blob: Blob; filename: string }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          // Create PDF with image dimensions
          const orientation = img.width > img.height ? 'landscape' : 'portrait';
          const pdf = new jsPDF({
            orientation,
            unit: 'px',
            format: [img.width, img.height]
          });
          
          // Add image to PDF
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }
          ctx.drawImage(img, 0, 0);
          const imgData = canvas.toDataURL('image/jpeg', 0.9);
          
          pdf.addImage(imgData, 'JPEG', 0, 0, img.width, img.height);
          
          const pdfBlob = pdf.output('blob');
          resolve({
            blob: pdfBlob,
            filename: generateFilename('pdf')
          });
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => reject(new Error('Failed to load image for PDF conversion'));
      img.src = URL.createObjectURL(imageBlob);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!capturedImage) return;

    setIsSubmitting(true);
    setError('');

    try {
      let fileToSave: { blob: Blob; filename: string };
      
      // Convert to PDF if selected, otherwise use image with better filename
      if (saveFormat === 'pdf') {
        fileToSave = await convertToPdf(capturedImage.blob);
      } else {
        fileToSave = {
          blob: capturedImage.blob,
          filename: generateFilename('jpg')
        };
      }

      await addExpense({
        category,
        subcategory: category === 'travel' ? subcategory : undefined,
        description,
        amount: amount ? parseFloat(amount) : undefined,
        date,
        file: fileToSave.blob,
        filename: fileToSave.filename,
      });

      setScannedCount(prev => prev + 1);
      setStep('success');
    } catch (err) {
      setError('Failed to save expense. Please try again.');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === 'success') {
    return (
      <div className="max-w-md mx-auto text-center py-12 animate-fade-in">
        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          Saved Successfully!
        </h2>
        <p className="text-white/60 mb-2">
          {scannedCount} receipt{scannedCount > 1 ? 's' : ''} scanned this session
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
          <button
            onClick={scanAnother}
            className="flex items-center justify-center space-x-2 px-6 py-4 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-xl font-medium hover:from-amber-400 hover:to-amber-500 transition-all"
          >
            <Camera className="w-5 h-5" />
            <span>Scan Another</span>
          </button>
          <button
            onClick={finishScanning}
            className="flex items-center justify-center space-x-2 px-6 py-4 bg-white/10 text-white border border-white/20 rounded-xl font-medium hover:bg-white/20 transition-all"
          >
            <span>Done</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
        
        <p className="text-white/40 text-sm mt-6">
          💡 Keep scanning to add more receipts
        </p>
      </div>
    );
  }

  if (step === 'camera') {
    return (
      <div className="max-w-2xl mx-auto animate-fade-in">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Scan Receipt</h1>
          <p className="text-white/60 mt-1">Position the receipt in the viewfinder</p>
        </div>
        <CameraCapture onCapture={handleCapture} onCancel={handleCancel} />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <button
        onClick={() => setStep('camera')}
        className="flex items-center space-x-2 text-white/70 hover:text-white mb-6 transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
        <span>Retake Photo</span>
      </button>

      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
        {/* Image Preview */}
        {capturedImage && (
          <div className="aspect-video bg-black flex items-center justify-center">
            <img
              src={URL.createObjectURL(capturedImage.blob)}
              alt="Captured receipt"
              className="max-h-full max-w-full object-contain"
            />
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="flex items-center space-x-2 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-300 text-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Category
            </label>
            <div className="grid grid-cols-4 gap-2">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setCategory(cat.value)}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${
                    category === cat.value
                      ? 'border-shuttle-sand bg-shuttle-sand/20'
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <span className="text-xl block">{cat.icon}</span>
                  <span className="text-xs text-white/70 mt-1 block truncate">{cat.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Travel Subcategory */}
          {category === 'travel' && (
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Travel Type
              </label>
              <div className="grid grid-cols-4 gap-2">
                {TRAVEL_SUBCATEGORIES.map((sub) => (
                  <button
                    key={sub.value}
                    type="button"
                    onClick={() => setSubcategory(sub.value)}
                    className={`p-2 rounded-xl border-2 text-center transition-all ${
                      subcategory === sub.value
                        ? 'border-shuttle-sand bg-shuttle-sand/20'
                        : 'border-white/20 hover:border-white/40'
                    }`}
                  >
                    <span className="text-lg block">{sub.icon}</span>
                    <span className="text-xs text-white/70 truncate block">{sub.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-white/80 mb-1">
              Description
            </label>
            <input
              id="description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Uber from airport to hotel"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
            />
          </div>

          {/* Amount and Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-white/80 mb-1">
                Amount ($)
              </label>
              <input
                id="amount"
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
              />
            </div>
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-white/80 mb-1">
                Date
              </label>
              <input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
              />
            </div>
          </div>

          {/* Save Format */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Save As
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSaveFormat('image')}
                className={`flex items-center justify-center space-x-2 p-3 rounded-xl border-2 transition-all ${
                  saveFormat === 'image'
                    ? 'border-shuttle-sand bg-shuttle-sand/20'
                    : 'border-white/20 hover:border-white/40'
                }`}
              >
                <FileImage className="w-5 h-5" />
                <span className="text-white">Image (JPG)</span>
              </button>
              <button
                type="button"
                onClick={() => setSaveFormat('pdf')}
                className={`flex items-center justify-center space-x-2 p-3 rounded-xl border-2 transition-all ${
                  saveFormat === 'pdf'
                    ? 'border-shuttle-sand bg-shuttle-sand/20'
                    : 'border-white/20 hover:border-white/40'
                }`}
              >
                <FileText className="w-5 h-5" />
                <span className="text-white">PDF</span>
              </button>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center space-x-2 py-4 px-6 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-xl font-medium hover:from-amber-400 hover:to-amber-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>Save Expense</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}





